import { Alert, Box, Button, Stack, Typography } from '@pulse/ui';
import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { GoogleIcon } from '../components/GoogleIcon';

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'You declined Google sign-in. Please try again to continue.',
  invalid_state: 'Your sign-in session expired. Please try again.',
  email_not_verified:
    'Your Google account email is not verified. Please verify it with Google and try again.',
  session_expired: 'Your session has expired. Please sign in again.',
};

const SESSION_EXPIRED_DISPLAY_MS = 3000;

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const errorCode = searchParams.get('error');

  React.useEffect(() => {
    if (errorCode !== 'session_expired') return;
    const timer = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('error');
          return next;
        },
        { replace: true },
      );
    }, SESSION_EXPIRED_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [errorCode, setSearchParams]);

  const from = (location.state as LocationState | null)?.from;
  const googleHref = `/api/v1/auth/google${from ? `?redirect=${encodeURIComponent(from)}` : ''}`;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, sm: 8, md: 12 } }}>
      <Stack
        spacing={3}
        alignItems="center"
        sx={{ maxWidth: 425, width: '100%', px: { xs: 2, sm: 3 } }}
      >
        <Box
          component="img"
          src="/shellkode-logo.svg"
          alt="Shellkode"
          sx={{ height: 32, width: 'auto' }}
        />
        <Typography variant="h4">Welcome to Pulse 2.0!</Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Sign in or sign up with your Google account to continue.
        </Typography>
        {errorCode && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {ERROR_MESSAGES[errorCode] ?? 'Something went wrong signing you in. Please try again.'}
          </Alert>
        )}
        {/* href (not onClick) — this must be a real full-page navigation so the browser
            follows the redirect chain to Google, not an intercepted fetch/XHR. */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          href={googleHref}
          startIcon={<GoogleIcon />}
        >
          Continue with Google
        </Button>
      </Stack>
    </Box>
  );
}
